import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  DATE_COMPARABLES,
  DATE_FORMAT,
  DATE_OPERATION_TYPES,
} from '../constants/DateTimeConstants';
import {
  MONTHLY_OPTIONS,
  WEEK_DAY_TO_NUMBER,
} from '../constants/ScenarioConstants';
import { deviation } from './commonUtil';

dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

/**
 * @returns Date
 */
export const getCurrentDate = () =>
  moment().format(DATE_FORMAT.defaultDateFormat1);

/**
 * Validates hours, minutes and seconds of toDate(endDate) with startDate whether date passed
 * isAfter
 * isBefore
 * isSameOrAfter
 * isSameOrBefore
 * @returns boolean
 */
export default function dateTimeValidator(
  startDate,
  dueDate,
  comparable,
  operation,
  amount,
  unit,
  hrs,
  mins,
  secs
) {
  const hours = !hrs ? '00' : hrs;
  const minutes = !mins ? '00' : mins;
  const seconds = !secs ? '00' : secs;
  const format = DATE_FORMAT.defaultDateFormat1;
  let toDate = `${moment(dueDate).format(format)} ${hours}:${
    !minutes ? '00' : minutes
  }:${!seconds ? '00' : seconds}`;
  if (operation || amount || unit || hours || minutes || seconds) {
    if (
      operation === DATE_OPERATION_TYPES.SUB ||
      operation === DATE_OPERATION_TYPES.SUBTRACT
    ) {
      toDate = `${moment(dueDate)
        .subtract(amount, unit)
        .format(format)} ${hours}:${minutes}:${seconds}`;
    }
    if (
      operation === DATE_OPERATION_TYPES.ADD ||
      operation === DATE_OPERATION_TYPES.ADDITION
    ) {
      toDate = `${moment(dueDate)
        .add(amount, unit)
        .format(format)} ${hours}:${minutes}:${seconds}`;
    }
  }
  if (comparable === DATE_COMPARABLES.IS_AFTER) {
    return startDate?.isAfter(toDate);
  }
  if (comparable === DATE_COMPARABLES.IS_BEFORE) {
    return startDate?.isBefore(toDate);
  }
  if (comparable === DATE_COMPARABLES.IS_SAME_OR_AFTER) {
    return startDate?.isSameOrAfter(toDate);
  }
  if (comparable === DATE_COMPARABLES.IS_SAME_OR_BEFORE) {
    return startDate?.isSameOrBefore(toDate);
  }
  return null;
}

/**
 * Returns the greeting based on the time the user logs in
 * 12 AM to 12 PM → Good Morning
 * 12.01 PM to 6 PM → Good Afternoon
 * 6.01 PM to 12 AM → Good Evening
 * @returns
 */
export function buildGreeting(currentTime) {
  const splitAfternoon = 12;
  const splitEvening = 18;

  if (currentTime > splitAfternoon && currentTime <= splitEvening) {
    return 'afternoon';
  }
  if (currentTime > splitEvening) {
    return 'evening';
  }
  return 'morning';
}

export function getDateFromDateDIM(numberValue) {
  const dateString = numberValue.toString();
  const year = +dateString.slice(0, 4);
  const month = +dateString.slice(4, 6);
  const day = +dateString.slice(6, 8);
  const hour = +dateString.slice(8, 10);

  if (hour) {
    return new Date(year, month - 1, day, hour);
  }
  return new Date(year, month - 1, day);
}

export const now = () => dayjs(Date.now()).utc();

export const calculateDaysToAdd = (dayOfWeek, latestSchedule, frequency) => {
  const _deviation = deviation(
    WEEK_DAY_TO_NUMBER[dayOfWeek],
    latestSchedule.day(),
    7
  );
  const targetDay = latestSchedule.add(_deviation, 'day');
  const targetDayWeek = targetDay.week();
  const currentWeek = latestSchedule.week();

  return targetDayWeek === currentWeek
    ? _deviation
    : 7 * (frequency - 1) + _deviation;
};

export const calculateWeeklyRecurrence = (
  latestSchedule,
  daysOfWeek,
  frequency
) => {
  const _deviations = daysOfWeek
    .map((dayOfWeek) =>
      calculateDaysToAdd(dayOfWeek, latestSchedule, frequency)
    ) // calculate the number of days to add to match the desired day of the week
    .filter(
      (value, index, self) => value !== 0 && self.indexOf(value) === index
    ); // filter out zero and duplicate values to ensure unique deviations

  if (!_deviations.length) return latestSchedule.add(frequency, 'week');

  return latestSchedule.add(Math.min(..._deviations), 'day');
};

export const calculateMonthlyRecurrence = (latestSchedule, repeatConfig) => {
  const { frequency } = repeatConfig;
  const {
    type: monthlyType,
    weekNumber,
    weekDay,
    date: monthlyDay,
  } = repeatConfig.monthlyOptions;
  let _next = null;

  if (monthlyType === MONTHLY_OPTIONS.DAY) {
    let nextMonth = latestSchedule.add(frequency, 'month');
    while (!_next) {
      const currentMonthDays = dayjs(nextMonth).daysInMonth();
      if (currentMonthDays >= monthlyDay) {
        _next = nextMonth.date(monthlyDay);
      }
      nextMonth = dayjs(nextMonth).add(1, 'month');
    }
  } else if (monthlyType === MONTHLY_OPTIONS.WEEK) {
    let nextDate = latestSchedule.add(frequency, 'month').startOf('month');
    let nextDayOfWeek = nextDate.format('dddd');
    while (nextDayOfWeek.toLowerCase() !== weekDay.toLowerCase()) {
      nextDate = nextDate.add(1, 'day');
      nextDayOfWeek = nextDate.format('dddd');
    }
    _next = nextDate.add((weekNumber - 1) * 7, 'day');
  } else if (monthlyType === MONTHLY_OPTIONS.LAST_DAY) {
    _next = latestSchedule.add(frequency, 'months').endOf('month');
  }

  return _next;
};

export const isOverDue = (dueDate) => {
  return moment(dueDate).isBefore(moment(), 'day');
};

export const getAlteredDateByAddingDates = (NumberOfDays) => {
  if (NumberOfDays) {
    return moment().add(NumberOfDays, 'days').format('YYYY-MM-DD');
  }
  return moment().format('YYYY-MM-DD');
};

/**
 * Add a time duration to a specific time.
 *
 * @param {*} Date
 * @param {*} value
 * @param {*} duration
 * @param {*} format
 * @returns
 */
export const addToDate = (
  date,
  value,
  durationType,
  format = DATE_FORMAT.defaultDateFormat1
) => moment(date).add(value, durationType).format(format);
